import { trackError } from '@vend/utilities'
import { LineItem } from '../../../../../models/line-item'
import { getInventory } from '../../../../product/products-resource/get-inventory'

export interface ClfOversellLine {
  lineItemLocalId: string
  productId: string
  productName: string
  fulfilmentOutletId: string
  quantity: number
  available: number
}

const getInventoryKey = (productId: string, outletId: string) =>
  `${productId}:${outletId}`

interface ClfLineItem {
  lineItem: LineItem
  productId: string
  fulfilmentOutletId: string
  quantity: number
}

const getClfLineItems = (lineItems: LineItem[]) =>
  lineItems.reduce<ClfLineItem[]>((clfLineItems, lineItem) => {
    const productId = lineItem.product?.id
    const fulfilmentOutletId = lineItem.fulfilmentOutletId
    const quantity = Number(lineItem.quantity)

    if (
      !productId ||
      !lineItem.product.has_inventory ||
      !fulfilmentOutletId ||
      !Number.isFinite(quantity) ||
      quantity <= 0
    ) {
      return clfLineItems
    }

    clfLineItems.push({
      lineItem,
      productId,
      fulfilmentOutletId,
      quantity,
    })

    return clfLineItems
  }, [])

export const findClfOversellLines = async (
  lineItems: LineItem[]
): Promise<ClfOversellLine[] | null> => {
  const clfLineItems = getClfLineItems(lineItems)

  if (clfLineItems.length === 0) {
    return []
  }

  const productIds = Array.from(
    new Set(clfLineItems.map(({ productId }) => productId))
  )
  const outletIds = Array.from(
    new Set(clfLineItems.map(({ fulfilmentOutletId }) => fulfilmentOutletId))
  )

  try {
    const inventoryLevels = await getInventory(productIds, {
      include_variants: false,
      location_ids: outletIds,
    })
    const availabilityByProductAndOutlet = new Map(
      inventoryLevels.map(inventoryLevel => [
        getInventoryKey(inventoryLevel.product_id, inventoryLevel.location_id),
        inventoryLevel.current_inventory_level,
      ])
    )
    const requestedByProductAndOutlet = clfLineItems.reduce(
      (requestedByKey, { productId, fulfilmentOutletId, quantity }) => {
        const inventoryKey = getInventoryKey(productId, fulfilmentOutletId)

        requestedByKey.set(
          inventoryKey,
          (requestedByKey.get(inventoryKey) ?? 0) + quantity
        )

        return requestedByKey
      },
      new Map<string, number>()
    )

    return clfLineItems.reduce<ClfOversellLine[]>(
      (
        oversellLines,
        { lineItem, productId, fulfilmentOutletId, quantity }
      ) => {
        const selectedProductId = productId
        const selectedOutletId = fulfilmentOutletId
        const inventoryKey = getInventoryKey(
          selectedProductId,
          selectedOutletId
        )
        const available = availabilityByProductAndOutlet.get(inventoryKey) ?? 0
        const requested = requestedByProductAndOutlet.get(inventoryKey) ?? 0

        if (requested <= available) {
          return oversellLines
        }

        oversellLines.push({
          lineItemLocalId: lineItem.localId,
          productId: selectedProductId,
          productName: lineItem.product.name,
          fulfilmentOutletId: selectedOutletId,
          quantity,
          available,
        })

        return oversellLines
      },
      []
    )
  } catch (error) {
    trackError('Failed to retrieve CLF inventory levels before payment', {
      data: { error },
    })
    return null
  }
}
